import { useState } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { RouterLink } from 'src/routes/components';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { useManagementUser, BuyPlanPromptDialog } from 'src/components/management-user';

// ----------------------------------------------------------------------

type ToolbarActionsProps = {
  isShowCreateBtn: boolean;
  isCreate: boolean;
  isView: boolean;
  actionhref: string;
  actionButtonLabel: string;
  actionButtonHandler: () => void;
  actionButtonIcon?: string;
  isDisableSync: boolean;
  secondaryActionButtonLabel?: string;
  secondaryActionButtonHandler?: () => void;
  secondaryActionButtonIcon?: string;
  isPrivacyPolicyUrls?: {
    message: string;
    links: { label: string; url: string }[];
  } | null;
  moduleName?: string;
};

// ----------------------------------------------------------------------

export function ToolbarActions({
  isShowCreateBtn,
  isCreate,
  isView,
  actionhref,
  actionButtonLabel,
  actionButtonHandler,
  actionButtonIcon,
  isDisableSync,
  secondaryActionButtonLabel,
  secondaryActionButtonHandler,
  secondaryActionButtonIcon,
  isPrivacyPolicyUrls,
  moduleName,
}: ToolbarActionsProps) {
  const { t } = useTranslate('translation');
  const { isManagement, hasPaidPlan } = useManagementUser();
  const [buyPlanOpen, setBuyPlanOpen] = useState(false);

  const isUserModule = moduleName?.toUpperCase() === 'USER';

  const paidFeatureModules = new Set([
    'CAMPAIGN',
    'CHANNEL',
    'WORKFLOW',
    'AI_AGENT',
    'USER',
  ]);

  const isPaidFeatureModule = moduleName ? paidFeatureModules.has(moduleName.toUpperCase()) : false;

  const isBlockedForManagement =
    isManagement &&
    !hasPaidPlan &&
    isPaidFeatureModule;

  const shouldShowNormalCreateBtn =
    isShowCreateBtn &&
    isCreate &&
    !isBlockedForManagement;

  const shouldShowBuyPlanButton =
    isShowCreateBtn &&
    Boolean(actionButtonLabel) &&
    isBlockedForManagement;

  const tooltipTitle = isUserModule
    ? t('ADDING_USER_REQUIRES_PAID_PLAN', {
        defaultValue: 'Adding a user requires a paid plan.',
      })
    : t('ADDING_CHANNEL_REQUIRES_PAID_USER', {
        defaultValue:
          'Adding a channel requires a paid user. Buy a plan to activate channels, campaigns, workflows and AI agents.',
      });

  const dialogTitle = isUserModule
    ? t('ADDING_USER_REQUIRES_PAID_PLAN', {
        defaultValue: 'Adding a user requires a paid plan.',
      })
    : t('ADDING_CHANNEL_REQUIRES_PAID_USER', {
        defaultValue: 'Adding a channel requires a paid user.',
      });

  const dialogDescription = isUserModule
    ? t('BUY_PLAN_TO_ACTIVATE_USERS', {
        defaultValue:
          'Buy a plan to activate users, channels, campaigns, workflows and AI agents.',
      })
    : t('BUY_PLAN_TO_ACTIVATE_FEATURES', {
        defaultValue:
          'Buy a plan to activate channels, campaigns, workflows and AI agents.',
      });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {shouldShowNormalCreateBtn && (
        <Button
          component={actionhref ? RouterLink : 'button'}
          href={actionhref || undefined}
          variant="contained"
          startIcon={<Iconify icon={actionButtonIcon || 'mingcute:add-line'} />}
          onClick={actionButtonHandler}
          sx={{
            background: '#50a28a',
            whiteSpace: 'nowrap',
            paddingX: 2,
            paddingY: 1,
            '&:hover': {
              background: '#40826d',
            },
          }}
          disabled={isDisableSync}
        >
          {actionButtonLabel}
        </Button>
      )}

      {shouldShowBuyPlanButton && (
        <>
          <Tooltip title={tooltipTitle}>
            <span>
              <Button
                variant="contained"
                startIcon={<Iconify icon={actionButtonIcon || 'mingcute:add-line'} />}
                onClick={() => setBuyPlanOpen(true)}
                sx={{
                  bgcolor: 'text.disabled',
                  color: 'common.white',
                  whiteSpace: 'nowrap',
                  px: 2,
                  py: 1,
                  opacity: 0.85,
                  '&:hover': {
                    bgcolor: 'text.secondary',
                  },
                }}
              >
                {actionButtonLabel}
              </Button>
            </span>
          </Tooltip>
          <BuyPlanPromptDialog
            open={buyPlanOpen}
            onClose={() => setBuyPlanOpen(false)}
            title={dialogTitle}
            description={dialogDescription}
          />
        </>
      )}
      {secondaryActionButtonLabel && secondaryActionButtonHandler && (
        <Button
          variant="outlined"
          startIcon={<Iconify icon={secondaryActionButtonIcon || 'mingcute:add-line'} />}
          onClick={secondaryActionButtonHandler}
        >
          {secondaryActionButtonLabel}
        </Button>
      )}

      {isPrivacyPolicyUrls && isPrivacyPolicyUrls?.links?.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            ml: { xs: 0, sm: 2 },
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {isPrivacyPolicyUrls.message ?? 'Please review the following policies:'}
          </Typography>
          {isPrivacyPolicyUrls.links.map((link, index) => (
            <Link
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {link.label}
              {index < isPrivacyPolicyUrls.links.length - 1 && <span>&nbsp;|&nbsp;</span>}
            </Link>
          ))}
        </Box>
      )}
    </Box>
  );
}
